import { atom } from "recoil";
import {
  selectorFamilyWithoutCache as selectorFamily,
  selectorWithoutCache as selector,
  selectorFamilyWithoutCache,
} from "util/RecoilWithoutCache";
import {
  ContractSymbolMapSelector,
  CurrentBusinessLineContractListState,
  MixContractInfoState,
} from "./Contract";
import { MixContractInfo, MixContractTickerInfo } from "../entity";
import { Decimal } from "decimal.js";

import { PendingState, SystemTimeState } from "./Base";

import { UserPreferenceSettingState } from "./User";
import { TimezoneEnum } from "../../constants/vars";
import { allStarSetSelector } from "./PriceAlert";
import { TopLevelContractList } from "api/ct";
import { createRefKeeper } from "util/index";
import { isEqual, pick } from "lodash-es";
import { queryClient } from "@app/client/queryClient";
import { futuresTicker } from "api/ssr/ticker";

export function createEmptyMixTickerInfo(): MixContractTickerInfo {
  return {
    status: 0,
    symbolId: "",
    baseVolume: "0",
    quoteVolume: "0",
    usdtVolume: "0",
    baseSymbol: "",
    bestAsk: "0",
    bestBid: "0",
    businessType: 0,
    capitalRate: "0",
    change: "0",
    before24Open: "0",
    high: "0",
    holding: 0,
    indexPrice: "0",
    low: "0",
    markPrice: "1",
    price: "0",
    pricedSymbol: "",
    symbolCode: "",
    vol: 0,
    nextSettleTime: "0",
    systemTime: "0",
    labelId: 0,
    settleFeeRateCycle: 8,
    ask5: "0",
    bid5: "0",
  };
}

export function getTickerKey(
  info: MixContractTickerInfo | MixContractInfo
): string {
  return info.symbolCode + info.businessLine;
}

export function getTickerByTickerKey(
  tickers: Map<string, MixContractTickerInfo>,
  tickerKey: string
) {
  let target = tickers.get(tickerKey);
  const template = createEmptyMixTickerInfo();

  if (!target) {
    target = template;
  } else {
    target = Object.assign(template, target);
  }

  return target;
}

/**
 * 所有Ticker数据
 */
export const MixContractAllTickersState = atom<MixContractTickerInfo[]>({
  key: "all-tickers-state-inner",
  default: [],
});

/**
 * last allTicker price rise fall
 */
export const MixContractAllTickerRiseFallState = atom({
  key: "mix-ct-all-ticker-rise-fall-state",
  default: [],
});

/**
 * 基于AllTickersState生成的map
 * 以businessType+symbolCode生成Key
 */
export const MixContractAllTickersMap = selector<
  Map<string, MixContractTickerInfo>
>({
  key: "all-tickers-map",
  get: ({ get }) => {
    const tickers = get(MixContractAllTickersState);

    return tickers.reduce((map, ticker) => {
      const key = getTickerKey(ticker);
      map.set(key, ticker);
      return map;
    }, new Map());
  },
});

/**
 * 生成一个symbolId=》tickerKey的map
 * tokenId=》tickerKeyMap
 */
export const MixContractTickerKeyMap = selector<Map<string, string>>({
  key: "ct-ticker-key-map",
  get: ({ get }) => {
    const contractList = get(CurrentBusinessLineContractListState);

    return contractList.reduce((map, ct) => {
      const tickerKey = getTickerKey(ct);
      map.set(ct.symbolId, tickerKey);
      return map;
    }, new Map());
  },
});

/**
 * 仅用于获取交易对Ticker信息（推荐使用，数据更新更快）
 */
export const getSymbolPriceBySymbolId = selectorFamily<
  MixContractTickerInfo,
  string
>({
  key: "token-ticker-selector",
  get:
    (symbolId: string) =>
    ({ get }) => {
      const tickerKeyMap = get(MixContractTickerKeyMap);
      const tickers = get(MixContractAllTickersMap);
      const originalTickers = get(MixContractAllTickersState);
      let findTicker = getTickerByTickerKey(
        tickers,
        tickerKeyMap.get(symbolId)
      );
      //这里做个兜底
      if (!isValidTicker(findTicker) && originalTickers.length > 0) {
        findTicker =
          originalTickers.find((item) => item.symbolId === symbolId) ||
          createEmptyMixTickerInfo();
      }
      return findTicker;
    },
});

/**
 * 当前业务线所有的ticker
 */
export const CurrentBusinessTickers = selector({
  key: "current-business-tickers",
  get: ({ get }) => {
    const contractList = get(CurrentBusinessLineContractListState);
    const tickersMap = get(MixContractAllTickersMap);
    const userPreferenceSettingState = get(UserPreferenceSettingState);
    return contractList.map((ct) => {
      const ticker =
        tickersMap.get(getTickerKey(ct)) || createEmptyMixTickerInfo();

      return {
        ...ticker,
        symbolId: ct.symbolId,
        symbolName: ct.baseSymbol + "/" + ct.pricedSymbol,
        symbolDisplayName: ct.symbolDisplayName,
        region: ct.region,
        chgUTC:
          userPreferenceSettingState.timezone === TimezoneEnum.UTC
            ? ticker.chgUTC
            : ticker.change,
      };
    });
  },
});

/**
 * 当前交易对的价格情况
 */
export const CurrentContractTicker = selector({
  key: "current-ct-ticker",
  get: ({ get }) => {
    const currentContract = get(MixContractInfoState);
    const tickersMap = get(MixContractAllTickersMap);
    const ticker =
      tickersMap.get(getTickerKey(currentContract)) ||
      createEmptyMixTickerInfo();
    // 直接补充最高买价和最低卖价
    return {
      ...ticker,
      region: currentContract.region,
      maxBuyPrice: new Decimal(+ticker.price || +ticker.markPrice || 0)
        .times(new Decimal(1).add(currentContract.buyLimitPriceRatio))
        .toFixed(currentContract.placeConfig.pricePlace), //公式 买入最高价 = 当前价格 * (1 + 买入限价比例)
      minSellPrice: new Decimal(+ticker.price || +ticker.markPrice || 0)
        .times(new Decimal(1).sub(currentContract.sellLimitPriceRatio))
        .toFixed(currentContract.placeConfig.pricePlace), //公式 卖出最低价 = 当前价格 * (1 - 卖出限价比例)
    };
  },
});

export const CurrentContractTickerFamily = selectorFamilyWithoutCache({
  key: "current-ct-ticker-family",
  get:
    (key: string) =>
    ({ get }) => {
      return get(CurrentContractTicker)[key];
    },
});

export const CurrentContractTickerRegion = selector({
  key: "current-ct-ticker-region",
  get: ({ get }) => get(CurrentContractTicker).region,
});

function isValidTicker(ticker: MixContractTickerInfo): boolean {
  return Boolean(ticker.baseSymbol && ticker.pricedSymbol && ticker.symbolCode);
}

/**
 * 获取/更新所有ticker
 * 更新时，顺便更新价格Map，触发仓位计算
 * 理论上ticker触发仓位计算，仓位计算触发资产计算
 */
export const MixContractAllTickersSelector = selector<MixContractTickerInfo[]>({
  key: "all-tickers-selector-public",
  get: ({ get }) => {
    return get(MixContractAllTickersState).filter((ticker) =>
      isValidTicker(ticker)
    );
  },
  set: ({ get, set }, tickers: MixContractTickerInfo[]) => {
    const pending = get(PendingState);
    const riseFall = get(MixContractAllTickerRiseFallState);
    const plots = tickers.map((item) => {
      const sfItem = riseFall.find((rsm) => rsm.symbolCode === item.symbolCode);
      let status;
      if (!sfItem || sfItem.price === item.price) {
        status = 0;
      } else if (Number(sfItem.price) < Number(item.price)) {
        status = 1;
      } else {
        status = -1;
      }
      return {
        ...item,
        risefallstatue: status,
      };
    });
    set(MixContractAllTickersState, plots);
    const simple = tickers.map((item) => {
      return {
        symbolCode: item.symbolCode,
        price: item.price,
      };
    });
    set(MixContractAllTickerRiseFallState, simple);
    if (pending) {
      set(PendingState, false);
    }
  },
});

/**
 * 当前交易对的Ticker信息
 */
export const MixContractCurrentTickerSelector = selector<MixContractTickerInfo>(
  {
    key: "current-ticker-selector-public",
    get: ({ get }) => {
      const list = get(MixContractAllTickersSelector);
      const contractInfo = get(MixContractInfoState);
      const tickerHttp = queryClient.getQueryData<MixContractTickerInfo>(
        futuresTicker.queryKey
      );
      return (
        list.find((ticker) => ticker.symbolCode === contractInfo.symbolCode) ||
        tickerHttp ||
        createEmptyMixTickerInfo()
      );
    },
  }
);

/**
 * 指定交易对的Ticker信息
 * */
export const MixContractCurrentTickerSelectorBySymbolId = selectorFamily<
  MixContractTickerInfo,
  string
>({
  key: "ticker-selector-public-by-symbol-id",
  get:
    (symbolId: string) =>
    ({ get }) => {
      const list = get(MixContractAllTickersSelector);
      return (
        list.find((ticker) => ticker.symbolId === symbolId) ||
        createEmptyMixTickerInfo()
      );
    },
});

export const MixContractCurrentTickerFamily = selectorFamilyWithoutCache<
  MixContractTickerInfo[keyof MixContractTickerInfo],
  keyof MixContractTickerInfo
>({
  key: "current-ticker-selector-public-family",
  get:
    (prop: keyof MixContractTickerInfo) =>
    ({ get }) => {
      return get(MixContractCurrentTickerSelector)[prop];
    },
});

export const SettleTimerSelectorNew = selector<number | null>({
  key: "settle-timer-selector-new",
  get: ({ get }) => {
    const ticker = get(MixContractCurrentTickerSelector);
    const systemTime = get(SystemTimeState);

    // 计算剩余时间
    const rest = Number(ticker.nextSettleTime) - Number(systemTime);
    // symbolType=2 交割合约不展示
    if (rest >= 0 && Number(ticker.nextSettleTime) > 0) {
      return Math.floor(rest / 1000);
    }
    return null;
  },
});

export const SettleTimerSelector = selector<number>({
  key: "settle-timer-selector",
  get: ({ get }) => {
    return get(SettleTimerSelectorNew) ?? 0;
  },
});

/**
 * 新增 所有 合约 Ticker 数据来源 tickerGroup
 */
export const FutureAllTickersGroupState = atom<MixContractTickerInfo[]>({
  key: "all-tickers-group-state",
  default: [],
});

export function findSpecial(
  list: TopLevelContractList,
  businessLine: number,
  symbolId: string
) {
  const find = list?.find((item) => item.businessLine === businessLine);
  if (find) {
    const finalItem = find.symbolDTOList.find((iSymbol) => {
      return iSymbol.symbolId === symbolId;
    });
    if (finalItem) {
      return finalItem;
    }
  }
  return null;
}

const tickerKeys = [
  "symbolCode",
  "symbolId",
  "symbolDisplayName",
  "businessLine",
  // 'usdtVolume',
  // 'price',
  // 'before24Open',
  "baseSymbol",
  "labelId",
  "labelId",
  "status",
  "ts",
  "ot",
  "scd",
] as const;
export type MixContractListTickerView = Pick<
  MixContractTickerInfo,
  (typeof tickerKeys)[number]
>;

const keepRef = createRefKeeper(isEqual);
// selectorFamily 不支持函数参数，所以只能用这种方式
export const changeCalculatorMap = new Map<
  string,
  (
    symbolCode: string,
    price: string | number,
    before24Open: string | number
  ) => number
>();
export const FutureTickersByConditionsSelector = selectorFamily<
  MixContractListTickerView[],
  any
>({
  key: "tickers-by-conditions-selector",
  get:
    ({
      searchKey,
      businessType,
      symbolType,
      plateType,
      sortType,
      sortOrder,
      changeCalculatorKey,
    }) =>
    ({ get }) => {
      let result = get(FutureAllTickersGroupState);
      if (businessType) {
        result = result.filter((t) => t.businessLine === businessType);
      } else {
        const starSet = get(allStarSetSelector);
        const _result = [];
        // 后端返回的数据是有排序的
        starSet.forEach((item) => {
          for (const r of result) {
            if (r.symbolId === item) {
              _result.push(r);
              break;
            }
          }
        });
        result = _result;
      }

      const contractSymbolMap = get(ContractSymbolMapSelector);

      if (searchKey) {
        result = result.filter((t) => {
          const config = contractSymbolMap[t.businessLine]?.[t.symbolId];
          const targetName =
            config?.symbolDisplayName || t.baseSymbol + t.pricedSymbol;
          return targetName?.includes(searchKey.toUpperCase());
        });
        result.sort((prev, next) => {
          const upperSearchKey = searchKey?.toUpperCase();
          const prevTargetName =
            prev?.symbolDisplayName || prev?.baseSymbol + prev?.pricedSymbol;
          const nextTargetName =
            next?.symbolDisplayName || next?.baseSymbol + next?.pricedSymbol;
          const prevIndex = prevTargetName?.indexOf(searchKey) || -1;
          const nextIndex = nextTargetName?.indexOf(searchKey) || -1;
          // 左币严格匹配的排序最靠前
          if (
            prev?.baseSymbol === upperSearchKey ||
            prevTargetName === upperSearchKey
          ) {
            return -1;
          } else {
            return prevIndex - nextIndex;
          }
        });
      }

      if (symbolType) {
        result = result.filter((t) => t.symbolType === symbolType);
      }

      if (+plateType >= 0) {
        result = result.filter((t) =>
          contractSymbolMap[t.businessLine]?.[t.symbolId]?.catalogIds?.includes(
            plateType
          )
        );
      }

      if (sortType) {
        const getSortFunctionByTag = (tag, tagValue) => {
          let sortFunc;
          switch (tag) {
            case "sortByName":
              sortFunc = (prev, next) => {
                const prevSymbolCode = prev.symbolCode;
                const nextSymbolCode = next.symbolCode;
                if (!tagValue) {
                  if (prevSymbolCode > nextSymbolCode) {
                    return 1;
                  } else if (prevSymbolCode === nextSymbolCode) {
                    return 0;
                  } else {
                    return -1;
                  }
                } else {
                  if (prevSymbolCode > nextSymbolCode) {
                    return -1;
                  } else if (prevSymbolCode === nextSymbolCode) {
                    return 0;
                  } else {
                    return 1;
                  }
                }
              };
              break;
            case "sortByDiscountPrice":
            case "sortByLastPrice":
              sortFunc = (prev, next) => {
                return tagValue
                  ? +prev.price - +next.price
                  : +next.price - +prev.price;
              };
              break;
            case "sortByRate":
              sortFunc = (prev, next) => {
                const calc = changeCalculatorMap.get(changeCalculatorKey);
                if (!calc) return 0;

                const prevValue =
                  calc(prev.symbolCode, prev.price || 0, prev.before24Open) /
                  100;
                const nextValue =
                  calc(next.symbolCode, next.price || 0, next.before24Open) /
                  100;
                return tagValue ? prevValue - nextValue : nextValue - prevValue;
              };
              break;
            case "sortByVolume":
              sortFunc = (prev, next) => {
                return tagValue
                  ? prev.usdtVolume - next.usdtVolume
                  : next.usdtVolume - prev.usdtVolume;
              };
              break;
            default:
              sortFunc = (prev, next) => {
                let prevCoin = prev.sortId,
                  nextCoin = next.sortId;
                if (prevCoin > nextCoin) {
                  return 1;
                } else if (prevCoin === nextCoin) {
                  return 0;
                } else {
                  return -1;
                }
              };
          }

          return sortFunc;
        };
        result = result.sort(getSortFunctionByTag(sortType, sortOrder));
      }

      return keepRef(
        result.map((t) => {
          const finalItem = contractSymbolMap[t.businessLine]?.[t.symbolId];
          return {
            ...pick(t, tickerKeys),
            symbolDisplayName: finalItem?.symbolDisplayName,
          };
        })
      );
    },
});

export const FutureTickerDataMapSelector = selector<
  Map<string, MixContractTickerInfo>
>({
  key: "ticker-data-map-selector",
  get: ({ get }) => {
    const list = get(FutureAllTickersGroupState);
    return new Map(list.map((t) => [t.symbolId, t]));
  },
});

export const FutureTickerFieldByKeySelector = selectorFamily({
  key: "ticker-field-by-key-selector",
  get:
    ({ id, key }: { id: string; key: keyof MixContractTickerInfo }) =>
    ({ get }) =>
      get(FutureTickerDataMapSelector).get(id)?.[key],
});
